import api, { fetchWithAuth } from './api';
import { ensureOk, okJson } from './httpError';

export type BookingStatus = 'Pending' | 'Confirmed' | 'Approved' | 'Cancelled' | 'Revoked';

export interface CommonArea {
  id: string;
  customer_id: string;
  name: string;
  description: string;
  capacity: number;
  is_active: boolean;
  requires_approval: boolean;
  max_advance_days: number;
  available_days_of_week: number[];
  max_duration_hours: number;
  max_bookings_per_month: number;
  opening_time: string;
  closing_time: string;
  // 'time_slot' (default): pick date + time, several bookings/day. 'whole_day': pick only a date,
  // one booking per area per day.
  booking_mode?: 'time_slot' | 'whole_day';
  // Per-weekday opening/closing windows (0=Sun..6=Sat); overrides opening_time/closing_time per day.
  operating_hours?: Array<{ weekday: number; open: string; close: string }>;
  rules: string;
  /**
   * Up to ten photos of the space (migrations 170/171). The apps have shown them in a carousel
   * since AreaComumView landed; the web typed them away, so its screens rendered a placeholder
   * next to a perfectly good photo.
   */
  photo_url?: string;
  photo_url_2?: string;
  photo_url_3?: string;
  photo_url_4?: string;
  photo_url_5?: string;
  photo_url_6?: string;
  photo_url_7?: string;
  photo_url_8?: string;
  photo_url_9?: string;
  photo_url_10?: string;
  reservation_fee?: number;
  late_cancellation_fee?: number;
  cancellation_deadline_hours?: number;
  min_advance_minutes?: number;
  active_reservation_cooldown_hours?: number;
  block_delinquent_units?: boolean;
  // Reservation refactor (migrations 218-219)
  checkin_checklist_enabled?: boolean;
  checklist_responsible_can_complete?: boolean;
  /** Modes this area accepts. Empty/absent means name-only — see effectiveGuestModes(). */
  guest_list_modes?: GuestMode[];
  guest_list_required?: boolean;
}

/**
 * How a guest was added to a booking's list. Mirrors models.ValidGuestListModes in
 * condo-manager — the backend rejects anything else, and an area additionally restricts which
 * of these it accepts.
 */
export type GuestMode = 'name_only' | 'visitor_registration' | 'import_visitors';

/**
 * Display labels for the three modes. Defined once here so the picker reads identically in the
 * normal view, the operational view and (translated from this list) the mobile apps.
 */
export const GUEST_MODE_LABELS: Record<GuestMode, string> = {
  name_only: 'Somente nome',
  visitor_registration: 'Cadastro de visitante',
  import_visitors: 'Importar dos meus visitantes',
};

export const GUEST_MODE_HINTS: Record<GuestMode, string> = {
  name_only: 'Apenas registra o nome na lista da portaria. Não gera credencial de acesso.',
  visitor_registration: 'Cria um cadastro de visitante real, com credencial de acesso para o período da reserva.',
  import_visitors: 'Escolhe um visitante já cadastrado pelo responsável, sem digitar novamente.',
};

/**
 * The modes an area actually accepts. An area created before migration 219 has the column empty
 * and must behave as name-only rather than refusing every guest — the same fallback the backend
 * applies in CommonArea.EffectiveGuestListModes.
 */
export function effectiveGuestModes(area?: Pick<CommonArea, 'guest_list_modes'> | null): GuestMode[] {
  const modes = (area?.guest_list_modes || []).filter((m): m is GuestMode => m in GUEST_MODE_LABELS);
  return modes.length > 0 ? modes : ['name_only'];
}

/** One line of an area's check-in checklist template. */
export interface CommonAreaChecklistItem {
  id: string;
  common_area_id: string;
  label: string;
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

/** Template line on its way to the API. `id` absent = new item. */
export interface ChecklistItemInput {
  id?: string;
  label: string;
  is_required: boolean;
}

/** One answered checklist line recorded against a booking. */
export interface CommonAreaChecklistResponse {
  id: string;
  booking_id: string;
  checklist_item_id?: string;
  item_label: string;
  checked: boolean;
  notes?: string;
  phase: string;
  completed_by_name?: string;
  completed_at: string;
}

export interface CheckinAnswer {
  item_id: string;
  checked: boolean;
  notes?: string;
}

export interface CheckinResult {
  booking_id: string;
  checked_in_at?: string;
  checked_in_by_name?: string;
  responses: CommonAreaChecklistResponse[];
}

/** A visitor the booking's responsible resident registered before, for the import mode. */
export interface ImportableVisitor {
  id: string;
  first_name: string;
  last_name: string;
  cpf?: string;
  phone?: string;
}

export interface AddGuestInput {
  name?: string;
  document?: string;
  phone?: string;
  email?: string;
  mode?: GuestMode;
  /** Required for import_visitors: the person being linked. */
  person_id?: string;
}

/** Customer-scoped third-party notification config (Configurações do Cliente). */
export interface CommonAreaEmailConfig {
  enabled: boolean;
  to_email: string;
  cc_emails: string[];
  notify_on_confirmed: boolean;
  notify_on_cancelled: boolean;
  extra_note?: string;
  updated_at?: string;
}

/** Copies allowed alongside the primary recipient — matches models.MaxCommonAreaEmailCopies. */
export const MAX_COMMON_AREA_EMAIL_COPIES = 3;

export interface CommonAreaBlackoutDate {
  id: string;
  common_area_id: string;
  blackout_date: string;
  reason?: string;
  created_by_name?: string;
  created_at: string;
}

export interface CommonAreaResource {
  id: string;
  common_area_id: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CommonAreaBookingGuest {
  id: string;
  booking_id: string;
  name: string;
  document?: string;
  added_at: string;
  // Reservation refactor (migration 219)
  mode?: GuestMode;
  person_id?: string;
  phone?: string;
  email?: string;
  added_by_name?: string;
}

export interface GuestListInfo {
  area_name: string;
  start_time: string;
  end_time: string;
  capacity: number;
  guests_count: number;
  guest_list_required?: boolean;
  rules?: string;
}

export type WaitlistStatus = 'waiting' | 'notified' | 'expired' | 'converted' | 'cancelled';

export interface CommonAreaWaitlistEntry {
  id: string;
  common_area_id: string;
  resource_id?: string;
  person_id: string;
  requested_start_time: string;
  requested_end_time: string;
  status: WaitlistStatus;
  notified_at?: string;
  claim_deadline?: string;
  converted_booking_id?: string;
  created_at: string;
  common_area?: CommonArea;
  resource?: CommonAreaResource;
  person?: BookingPerson;
}

export interface BookingPerson {
  id: string;
  first_name: string;
  last_name: string;
  profile_picture_url?: string;
  identity_photo?: {
    photo_url?: string;
  };
  roles?: Array<{
    role_metadata?: {
      unit_id?: string;
      unit_ids?: string[];
      unit_number?: string;
      block_name?: string;
    };
  }>;
}

export interface CommonAreaBooking {
  id: string;
  common_area_id: string;
  person_id?: string;
  performed_by_person_id?: string;
  performed_by_user_id?: string;
  performed_by_name?: string;
  start_time: string;
  end_time: string;
  purpose: string;
  notes: string;
  status: BookingStatus;
  approved_by?: string;
  approved_by_user_id?: string;
  approved_by_name?: string;
  approved_at?: string;
  revoked_by?: string;
  revoked_by_user_id?: string;
  revoked_by_name?: string;
  revoked_at?: string;
  revoked_reason?: string;
  late_cancellation_fee_applied?: boolean;
  late_cancellation_fee_amount?: number;
  guest_list_token?: string;
  // Unit snapshot frozen at booking time (migration 217) — do NOT re-derive from person.roles,
  // which follows the resident if they move unit.
  unit_id?: string;
  unit_label?: string;
  checked_in_at?: string;
  checked_in_by_name?: string;
  resource_id?: string;
  resource?: CommonAreaResource;
  created_at: string;
  updated_at: string;
  common_area?: CommonArea;
  person?: BookingPerson;
}

export interface CreateBookingPayload {
  customer_id: string;
  person_id?: string;
  resource_id?: string;
  start_time: string;
  end_time: string;
  purpose: string;
  notes?: string;
}

export interface BookingFilters {
  from?: string;
  to?: string;
  status?: BookingStatus | '';
}

/**
 * The áreas list out of whatever the endpoint answered.
 *
 * Callers render `.filter(...)` straight off this, so it must always be a list: the reservations
 * page trusted the body to be an array and swallowed every failure, and an empty (or object-shaped)
 * result then reached "Nova Reserva" as a dropdown with nothing in it — which reads exactly like
 * "I have not picked an área yet" and leaves the operator clicking a dead calendar.
 */
export function commonAreasFromPayload(payload: unknown): CommonArea[] {
  if (Array.isArray(payload)) return payload as CommonArea[];
  const data = (payload as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as CommonArea[]) : [];
}

export const commonAreaReservationService = {
  async createBooking(areaId: string, payload: CreateBookingPayload): Promise<CommonAreaBooking> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const error = new Error(err.message || 'Erro ao criar reserva') as Error & { status?: number };
      error.status = res.status;
      throw error;
    }
    return res.json();
  },

  async getBookingsForArea(areaId: string, filters: BookingFilters = {}): Promise<CommonAreaBooking[]> {
    const params = new URLSearchParams();
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    const query = params.toString() ? `?${params}` : '';
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings${query}`);
    if (!res.ok) throw new Error('Erro ao carregar reservas da área');
    return res.json();
  },

  async getAllBookings(customerId: string, filters: BookingFilters = {}): Promise<CommonAreaBooking[]> {
    const params = new URLSearchParams();
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.status) params.set('status', filters.status);
    const query = params.toString() ? `?${params}` : '';
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/common-area-bookings${query}`);
    if (!res.ok) throw new Error('Erro ao carregar reservas');
    return res.json();
  },

  async getMyBookings(customerId: string): Promise<CommonAreaBooking[]> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/common-area-bookings/me`);
    if (!res.ok) throw new Error('Erro ao carregar suas reservas');
    return res.json();
  },

  async approveBooking(areaId: string, bookingId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/approve`, {
      method: 'PUT',
    });
    await ensureOk(res, 'Erro ao aprovar reserva');
  },

  async revokeBooking(areaId: string, bookingId: string, reason: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/revoke`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    await ensureOk(res, 'Erro ao revogar reserva');
  },

  async cancelBooking(areaId: string, bookingId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/cancel`, {
      method: 'PUT',
    });
    await ensureOk(res, 'Erro ao cancelar reserva');
  },

  async getBlackoutDates(areaId: string): Promise<CommonAreaBlackoutDate[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/blackout-dates`);
    if (!res.ok) throw new Error('Erro ao carregar bloqueios de data');
    const json = await res.json();
    return json.data || [];
  },

  async createBlackoutDate(areaId: string, blackoutDate: string, reason?: string): Promise<CommonAreaBlackoutDate> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/blackout-dates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blackout_date: blackoutDate, reason }),
    });
    await ensureOk(res, 'Erro ao criar bloqueio');
    const json = await res.json();
    return json.data;
  },

  async deleteBlackoutDate(areaId: string, blackoutId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/blackout-dates/${blackoutId}`, {
      method: 'DELETE',
    });
    await ensureOk(res, 'Erro ao remover bloqueio');
  },

  async getResources(areaId: string): Promise<CommonAreaResource[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/resources`);
    if (!res.ok) throw new Error('Erro ao carregar recursos');
    const json = await res.json();
    return json.data || [];
  },

  async createResources(areaId: string, names: string[]): Promise<CommonAreaResource[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/resources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names }),
    });
    await ensureOk(res, 'Erro ao criar recurso(s)');
    const json = await res.json();
    return json.data || [];
  },

  async updateResource(areaId: string, resourceId: string, updates: Partial<Pick<CommonAreaResource, 'name' | 'is_active' | 'sort_order'>>): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/resources/${resourceId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    await ensureOk(res, 'Erro ao atualizar recurso');
  },

  async deleteResource(areaId: string, resourceId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/resources/${resourceId}`, {
      method: 'DELETE',
    });
    await ensureOk(res, 'Erro ao remover recurso');
  },

  async joinWaitlist(areaId: string, startTime: string, endTime: string, resourceId?: string): Promise<CommonAreaWaitlistEntry> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ start_time: startTime, end_time: endTime, resource_id: resourceId }),
    });
    await ensureOk(res, 'Erro ao entrar na fila de espera');
    const json = await res.json();
    return json.data;
  },

  async getWaitlistForArea(areaId: string): Promise<CommonAreaWaitlistEntry[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/waitlist`);
    if (!res.ok) throw new Error('Erro ao carregar fila de espera');
    const json = await res.json();
    return json.data || [];
  },

  async getMyWaitlist(customerId: string): Promise<CommonAreaWaitlistEntry[]> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/common-area-waitlist/me`);
    if (!res.ok) throw new Error('Erro ao carregar sua fila de espera');
    return res.json();
  },

  async leaveWaitlist(areaId: string, entryId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/waitlist/${entryId}`, {
      method: 'DELETE',
    });
    await ensureOk(res, 'Erro ao sair da fila de espera');
  },

  async claimWaitlistEntry(areaId: string, entryId: string): Promise<CommonAreaBooking> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/waitlist/${entryId}/claim`, {
      method: 'POST',
    });
    await ensureOk(res, 'Erro ao reivindicar vaga');
    const json = await res.json();
    return json.data;
  },

  async getGuests(areaId: string, bookingId: string): Promise<CommonAreaBookingGuest[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/guests`);
    if (!res.ok) throw new Error('Erro ao carregar lista de convidados');
    const json = await res.json();
    return json.data || [];
  },

  // ── Check-in checklist template ──────────────────────────────────────────────

  async getChecklistItems(areaId: string): Promise<CommonAreaChecklistItem[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/checklist-items`);
    const json = await okJson<{ data?: CommonAreaChecklistItem[] }>(res, 'Erro ao carregar o checklist');
    return json.data || [];
  },

  /** Saves the whole template in one call — that is how the admin screen edits it. */
  async replaceChecklistItems(areaId: string, items: ChecklistItemInput[]): Promise<CommonAreaChecklistItem[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/checklist-items`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    const json = await okJson<{ data?: CommonAreaChecklistItem[] }>(res, 'Erro ao salvar o checklist');
    return json.data || [];
  },

  async getChecklistResponses(areaId: string, bookingId: string): Promise<CommonAreaChecklistResponse[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/checklist-responses`);
    const json = await okJson<{ data?: CommonAreaChecklistResponse[] }>(res, 'Erro ao carregar o checklist da reserva');
    return json.data || [];
  },

  // ── Check-in ────────────────────────────────────────────────────────────────
  //
  // Two routes, deliberately not one. The staff route is role-gated on the area; the resident
  // route is authorized by booking OWNERSHIP and carries no area id. Sending a resident through
  // the staff route 403s, and the reverse would skip the ownership check.

  async completeCheckin(areaId: string, bookingId: string, answers: CheckinAnswer[]): Promise<CheckinResult> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/checkin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers }),
    });
    const json = await okJson<{ data: CheckinResult }>(res, 'Erro ao registrar o check-in');
    return json.data;
  },

  async completeCheckinAsResponsible(bookingId: string, answers: CheckinAnswer[]): Promise<CheckinResult> {
    const res = await fetchWithAuth(`/api/v1/common-area-bookings/${bookingId}/checkin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers }),
    });
    const json = await okJson<{ data: CheckinResult }>(res, 'Erro ao registrar o check-in');
    return json.data;
  },

  // ── Guest list ──────────────────────────────────────────────────────────────

  async addGuestAsStaff(areaId: string, bookingId: string, input: AddGuestInput): Promise<CommonAreaBookingGuest> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/guests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const json = await okJson<{ data: CommonAreaBookingGuest }>(res, 'Erro ao adicionar convidado');
    return json.data;
  },

  async deleteGuestAsStaff(areaId: string, bookingId: string, guestId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/guests/${guestId}`, {
      method: 'DELETE',
    });
    await ensureOk(res, 'Erro ao remover convidado');
  },

  async getImportableVisitors(areaId: string, bookingId: string): Promise<ImportableVisitor[]> {
    const res = await fetchWithAuth(`/api/v1/common-areas/${areaId}/bookings/${bookingId}/importable-visitors`);
    const json = await okJson<{ data?: ImportableVisitor[] }>(res, 'Erro ao carregar visitantes do responsável');
    return json.data || [];
  },

  async getMyBookingGuests(bookingId: string): Promise<CommonAreaBookingGuest[]> {
    const res = await fetchWithAuth(`/api/v1/common-area-bookings/${bookingId}/guests`);
    const json = await okJson<{ data?: CommonAreaBookingGuest[] }>(res, 'Erro ao carregar seus convidados');
    return json.data || [];
  },

  async addMyBookingGuest(bookingId: string, input: AddGuestInput): Promise<CommonAreaBookingGuest> {
    const res = await fetchWithAuth(`/api/v1/common-area-bookings/${bookingId}/guests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const json = await okJson<{ data: CommonAreaBookingGuest }>(res, 'Erro ao adicionar convidado');
    return json.data;
  },

  async deleteMyBookingGuest(bookingId: string, guestId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/common-area-bookings/${bookingId}/guests/${guestId}`, {
      method: 'DELETE',
    });
    await ensureOk(res, 'Erro ao remover convidado');
  },
};

/**
 * Third-party reservation e-mail config, edited under Configurações do Cliente.
 *
 * Separate object from commonAreaReservationService because it is customer-scoped admin
 * configuration, not a reservation operation — the same split the backend makes between
 * CommonAreaHandler and CustomerHandler.
 */
export const commonAreaEmailConfigApi = {
  async get(customerId: string): Promise<CommonAreaEmailConfig> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/common-area-email-config`);
    const json = await okJson<{ data: CommonAreaEmailConfig }>(res, 'Erro ao carregar a configuração de e-mail');
    return json.data;
  },

  async update(customerId: string, config: CommonAreaEmailConfig): Promise<CommonAreaEmailConfig> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/common-area-email-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    const json = await okJson<{ data: CommonAreaEmailConfig }>(res, 'Erro ao salvar a configuração de e-mail');
    return json.data;
  },

  /** Sends one sample reservation e-mail, rendered by the real templates. */
  async sendTest(customerId: string, toEmail: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/common-area-email-config/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to_email: toEmail }),
    });
    await ensureOk(res, 'Erro ao enviar o e-mail de teste');
  },
};

/** Public, unauthenticated guest-list link — mirrors preRegistrationApi's validate/submit pattern. */
export const guestListApi = {
  async validate(token: string): Promise<GuestListInfo> {
    const response = await api.get(`/public/common-area-bookings/guest-list/${token}`);
    return response.data;
  },

  async addGuest(token: string, name: string, document?: string): Promise<CommonAreaBookingGuest> {
    const response = await api.post(`/public/common-area-bookings/guest-list/${token}`, { name, document });
    return response.data.data;
  },
};
